import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, UserPlus, Mail, MoreHorizontal, Shield, Trash2, Loader2, Search, CheckCircle2 } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { usersAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const ROLE_COLORS = {
  super_admin: 'bg-purple-50 text-purple-700 border-purple-200',
  admin: 'bg-red-50 text-red-700 border-red-200',
  editor: 'bg-blue-50 text-blue-700 border-blue-200',
  producer: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  journalist: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  photographer: 'bg-green-50 text-green-700 border-green-200',
  designer: 'bg-pink-50 text-pink-700 border-pink-200',
  data_journalist: 'bg-violet-50 text-violet-700 border-violet-200',
  social_media_manager: 'bg-sky-50 text-sky-700 border-sky-200',
  seo_specialist: 'bg-orange-50 text-orange-700 border-orange-200',
  ad_operations: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  subscription_manager: 'bg-teal-50 text-teal-700 border-teal-200',
  analytics_analyst: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  hr_admin: 'bg-lime-50 text-lime-700 border-lime-200',
  finance_controller: 'bg-amber-50 text-amber-700 border-amber-200',
  legal_counsel: 'bg-rose-50 text-rose-700 border-rose-200',
  external_contributor: 'bg-gray-50 text-gray-600 border-gray-200',
  moderator: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  reader: 'bg-slate-50 text-slate-600 border-slate-200',
};

const AVATAR_COLORS = ['#B89B5E', '#1E5A8A', '#1F7A5A', '#7C3AED', '#B3261E', '#0891B2', '#D97706', '#059669'];

export default function TeamSetting() {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('journalist');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const res = await usersAPI.list({ limit: 100 });
      setMembers(res.data.users || []);
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!inviteName.trim()) { toast.error('Enter a name'); return; }
    setInviting(true);
    try {
      await usersAPI.create({ email: inviteEmail, password: 'Welcome123!', full_name: inviteName, role: inviteRole });
      toast.success(`Invited ${inviteName} as ${inviteRole}`);
      setInviteEmail(''); setInviteName('');
      await loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team member');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await usersAPI.update(member.id, { role: newRole });
      toast.success(`Updated ${member.full_name}'s role to ${newRole}`);
      await loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (member) => {
    if (member.id === currentUser?.id) { toast.error("You can't remove yourself"); return; }
    if (!window.confirm(`Remove ${member.full_name} from the team?`)) return;
    try {
      await usersAPI.delete(member.id);
      toast.success(`Removed ${member.full_name}`);
      await loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const filtered = members.filter(m =>
    !search || m.full_name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Exclude readers from team view
  const staffMembers = filtered.filter(m => m.role !== 'reader');

  return (
    <div className="space-y-6">
      {/* Add team member */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#B89B5E]" /> Add Team Member
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Full Name</Label>
              <Input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="John Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Email Address</Label>
              <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="john@neuralnews.com" type="email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="producer">Producer</SelectItem>
                  <SelectItem value="journalist">Journalist</SelectItem>
                  <SelectItem value="photographer">Photographer</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="data_journalist">Data Journalist</SelectItem>
                  <SelectItem value="social_media_manager">Social Media Manager</SelectItem>
                  <SelectItem value="seo_specialist">SEO Specialist</SelectItem>
                  <SelectItem value="ad_operations">Ad Operations</SelectItem>
                  <SelectItem value="subscription_manager">Subscription Manager</SelectItem>
                  <SelectItem value="analytics_analyst">Analytics Analyst</SelectItem>
                  <SelectItem value="hr_admin">HR Admin</SelectItem>
                  <SelectItem value="finance_controller">Finance Controller</SelectItem>
                  <SelectItem value="legal_counsel">Legal Counsel</SelectItem>
                  <SelectItem value="external_contributor">External Contributor</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleInvite} disabled={inviting} className="bg-[#121316] text-white hover:bg-[#1B1D22] w-full">
                {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                {inviting ? 'Adding...' : 'Add Member'}
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-[#5B616E] mt-2">New members get a default password (Welcome123!) and must change it on first use.</p>
        </CardContent>
      </Card>

      {/* Team list */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-[#1E5A8A]" /> Team Members
              <Badge variant="secondary" className="text-[10px] ml-1">{staffMembers.length}</Badge>
            </CardTitle>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5B616E]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-8 h-8 text-xs w-48" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#5B616E]" />
            </div>
          ) : staffMembers.length === 0 ? (
            <p className="text-center text-sm text-[#5B616E] py-6">No team members found</p>
          ) : (
            <div className="space-y-0 divide-y divide-[#E7E0D6]">
              {staffMembers.map((member, i) => (
                <div key={member.id || i} className="flex items-center justify-between py-3.5">
                  <div className="flex items-center gap-3">
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                      >
                        {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{member.full_name}</p>
                        {member.id === currentUser?.id && (
                          <Badge variant="outline" className="text-[#B89B5E] border-[#B89B5E]/30 bg-[#B89B5E]/5 text-[9px]">You</Badge>
                        )}
                        {member.mfa_enabled && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" title="2FA enabled" />
                        )}
                      </div>
                      <p className="text-xs text-[#5B616E]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={cn('text-[10px] capitalize', ROLE_COLORS[member.role] || ROLE_COLORS.reader)}>
                      {member.role?.replace(/_/g, ' ')}
                    </Badge>
                    {member.id !== currentUser?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="w-4 h-4 text-[#5B616E]" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[180px] max-h-80 overflow-y-auto">
                          {[
                            { id: 'admin', label: 'Admin' },
                            { id: 'editor', label: 'Editor' },
                            { id: 'producer', label: 'Producer' },
                            { id: 'journalist', label: 'Journalist' },
                            { id: 'photographer', label: 'Photographer' },
                            { id: 'designer', label: 'Designer' },
                            { id: 'data_journalist', label: 'Data Journalist' },
                            { id: 'social_media_manager', label: 'Social Media Mgr' },
                            { id: 'seo_specialist', label: 'SEO Specialist' },
                            { id: 'ad_operations', label: 'Ad Operations' },
                            { id: 'subscription_manager', label: 'Subscription Mgr' },
                            { id: 'analytics_analyst', label: 'Analytics Analyst' },
                            { id: 'hr_admin', label: 'HR Admin' },
                            { id: 'finance_controller', label: 'Finance Controller' },
                            { id: 'legal_counsel', label: 'Legal Counsel' },
                            { id: 'external_contributor', label: 'External Contributor' },
                            { id: 'moderator', label: 'Moderator' },
                          ].filter(r => r.id !== member.role).map(r => (
                            <DropdownMenuItem key={r.id} className="text-xs" onClick={() => handleRoleChange(member, r.id)}>
                              <Shield className="w-3.5 h-3.5 mr-2" /> {r.label}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem className="text-xs text-red-600 focus:text-red-600" onClick={() => handleRemove(member)}>
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
